import type { AxisSelection } from '@atlas/types'
import { AxisCode } from '@atlas/types'
import { toggleFilterValue } from '@atlas/ui-components'
import { ALL_CHANNELS } from '@atlas/ui-domain'
import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import type { ProductPage as ProductPageData } from '@/domain/explore.types'
import { useBrands, useProductPage, useProducts } from '@/hooks/useExplore'
import { isNotFound } from '@/lib/read-error'
import { DEFAULT_PRODUCT_FILTER_AXES, ProductFilterRail } from './ProductFilterRail'
import { ProductMainTabs } from './ProductMainTabs'
import { PRODUCT_PAGE_EYEBROW, ProductPageLayout } from './ProductPageLayout'
import { ProductPageSkeleton } from './ProductPageSkeleton'
import { brandChoices, geoLevelsOf, marketsOf, productChoices, regionsOf } from './product-hierarchy'
import { brandKitEmptyLabel, campaignsEmptyLabel, textsEmptyLabel } from './product-page-words'
import { ResolvingForBar } from './ResolvingForBar'
import { useProductSections } from './useProductSections'

/**
 * What the SERVER resolved on geography, not what the URL asked for: a stale or retired
 * code degrades the page to unscoped, and every read on it has to be asked the same
 * question the page states it is answering. The ladder starts at the value resolved for,
 * so its head is that value.
 */
const resolvedGeo = (page: ProductPageData | undefined): string | null =>
	page?.appliesTo[AxisCode.GEOGRAPHY]?.[0] ?? null

/**
 * The geography the page stands at while the read for it is still in flight: what the URL
 * asked for, as long as the page on screen offers it — clearing the scope always does, since
 * unscoped is always offered. A code the page does not offer is one the page cannot speak
 * for (a hand-edited or retired one), so it holds its last resolved value until the server
 * rules on it.
 */
const requestedGeo = (page: ProductPageData | undefined, requested: string | null): string | null => {
	if (requested === null) return null
	const offered = page?.resolvableValues[AxisCode.GEOGRAPHY]?.some((v) => v.code === requested)
	return offered ? requested : resolvedGeo(page)
}

/** The resolved geography's label, from the options the server offered for the axis. */
const marketLabelOf = (page: ProductPageData): string | null => {
	const code = resolvedGeo(page)
	return (code ? page.resolvableValues[AxisCode.GEOGRAPHY]?.find((v) => v.code === code)?.label : null) ?? null
}

/**
 * The Rules-explorer link for a coverage context: one query parameter per axis, its codes
 * comma-joined. Built from the SAME context the card was read for, so following the link
 * can never ask a narrower or wider question than the card just answered.
 */
const rulesLinkTo = (context: AxisSelection): string => {
	const params = new URLSearchParams(Object.entries(context).map(([axisCode, codes]) => [axisCode, codes.join(',')]))
	return `/rules?${params.toString()}`
}

/**
 * The geography the page resolves for, read from and written back to the URL.
 *
 * In the URL rather than component state so a resolved view is shareable and the back
 * button steps through the narrowing. ONE code for both levels of the axis — a region or a
 * market, whichever was picked — so a link can never state a region and a market that
 * disagree, and the links the funnel and the brand page already hand over keep working.
 *
 * The rail's filters are deliberately NOT here: they narrow what the page lists rather than
 * what it resolves, and a shared link should carry the question rather than the reader's
 * last sift through the answer.
 */
function useGeoScope() {
	const [params, setParams] = useSearchParams()
	return {
		requested: params.get('market'),
		setScope: (code: string | null) => {
			const next = new URLSearchParams(params)
			if (code) next.set('market', code)
			else next.delete('market')
			setParams(next)
		},
	}
}

/** The rail's state: which axis groups are shown, and what is selected on each. */
function useContentFilters() {
	const [selection, setSelection] = useState<AxisSelection>({})
	const [shownAxisCodes, setShownAxisCodes] = useState<string[]>(DEFAULT_PRODUCT_FILTER_AXES)
	return {
		selection,
		shownAxisCodes,
		toggle: (axisCode: string, valueCode: string) => setSelection((s) => toggleFilterValue(s, axisCode, valueCode)),
		addAxis: (axisCode: string) =>
			setShownAxisCodes((codes) => (codes.includes(axisCode) ? codes : [...codes, axisCode])),
	}
}

interface HierarchyProps {
	page: ProductPageData
	/** The geography the page agrees on — region or market code, `null` when unscoped. */
	scope: string | null
	onScopeChange: (code: string | null) => void
	isResolving: boolean
}

/**
 * The hierarchy bar, wired: the two geography levels split out of the one resolved scope,
 * and the brand and product levels read from the catalogue.
 *
 * Picking a brand only refills the product level — the page still shows the product it was
 * opened for, because a brand is not a page here. Picking a product IS the navigation, and
 * carries the geography scope with it so the next product opens resolved the same way.
 */
function ProductHierarchy({ page, scope, onScopeChange, isResolving }: HierarchyProps) {
	const navigate = useNavigate()
	const { search } = useLocation()
	const { data: brands = [] } = useBrands()
	const { data: products = [] } = useProducts()
	/**
	 * A brand picked to browse, remembered against the product it was picked on: navigating
	 * to a product of another brand makes the pick moot, and carrying it over would leave the
	 * bar naming a brand the open product does not belong to.
	 */
	const [picked, setPicked] = useState<{ productId: string; brandId: string } | null>(null)

	const geoValues = page.resolvableValues[AxisCode.GEOGRAPHY] ?? []
	const { region, market } = geoLevelsOf(geoValues, scope)
	const productId = page.product.productId
	const ownBrandId = page.brand?.brandId ?? null
	const brandId = picked?.productId === productId ? picked.brandId : ownBrandId

	return (
		<ResolvingForBar
			regions={regionsOf(geoValues)}
			region={region}
			onRegionChange={onScopeChange}
			markets={marketsOf(geoValues, region)}
			market={market}
			// Clearing the market steps back up to the region rather than to unscoped: the
			// scope is one code, and the region the market sits in is still resolved.
			onMarketChange={(code) => onScopeChange(code ?? region)}
			brands={brandChoices(brands)}
			brand={brandId}
			onBrandChange={(id) => setPicked(id ? { productId, brandId: id } : null)}
			products={productChoices(products, brandId)}
			// Nothing is resolved on this level while another brand is being browsed.
			product={brandId === ownBrandId ? productId : null}
			onProductChange={(id) => void navigate(`/explore/product/${id}${search}`)}
			isResolving={isResolving}
		/>
	)
}

/**
 * `Region / Market / Brand / Product` — the same hierarchy the bar resolves for, as a path.
 * Reads off the scope the bar shows, so the two can never state different markets.
 *
 * The brand segment links back, carrying the geography scope so stepping up narrows the
 * brand page the same way.
 */
function ProductBreadcrumb({ page, scope }: { page: ProductPageData; scope: string | null }) {
	const geoValues = page.resolvableValues[AxisCode.GEOGRAPHY] ?? []
	const { region, market } = geoLevelsOf(geoValues, scope)
	const labelOf = (code: string) => geoValues.find((v) => v.code === code)?.label ?? code
	const brandName = page.brand?.name ?? 'Brand'
	const scopeQuery = scope ? `?${new URLSearchParams({ market: scope }).toString()}` : ''
	const geoSegments = [region, market].filter((code): code is string => code !== null)

	return (
		<nav
			aria-label="Breadcrumb"
			className="mb-4 flex items-center gap-1.5 font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle"
		>
			{geoSegments.map((code) => (
				<span key={code} className="flex items-center gap-1.5">
					{labelOf(code)}
					<span aria-hidden>/</span>
				</span>
			))}
			{page.brand ? (
				<Link to={`/explore/brand/${page.brand.brandId}${scopeQuery}`} className="hover:text-ds-text">
					{brandName}
				</Link>
			) : (
				<span>{brandName}</span>
			)}
			<span aria-hidden>/</span>
			<span className="text-ds-text">{page.product.name}</span>
		</nav>
	)
}

/**
 * The Product page — one product, resolved for one place in the catalogue hierarchy
 * (Region / Market / Brand / Product), with its content narrowed by the filter rail beside
 * it.
 *
 * Two kinds of input, deliberately separated. The HIERARCHY is the page's identity and its
 * one scope: it is single-valued, it lives in the URL, and changing it re-resolves the page
 * (what the brand kit applies to, which assets are delivered there, which rules cover it).
 * The RAIL's filters narrow what the page lists, so they are multi-valued, local, and cost
 * no page read.
 *
 * Both reach EVERY section — the kit, the texts, the delivered assets and the rules — by
 * one of two routes: the reads that can express the narrowing take it as a filter, and the
 * ones that cannot (applicability is not descendant matching) are narrowed on the tags they
 * return. Two cards answering different questions side by side would be the bug either way.
 *
 * Real data: product identity, its brand, the assets classified to the scope and filters,
 * its brand's kit files grouped by the role they play, the texts classified to it, and the
 * rules covering it. Sample data: `product-page-sample.ts` and `explore-sample.ts`,
 * each behind a `SampleDataNotice`.
 *
 * Separate reads, not one composed response: rules live in rule-service and file metadata
 * in file-service, both of which already depend on classification-service (ADR-0010), so
 * composing them server-side would close a dependency cycle. Each resolves independently,
 * so a slow secondary read costs its own card a skeleton rather than blanking the page.
 *
 * Two waits, treated differently. An empty page has nothing to keep, so the first read gets
 * a skeleton in the page's own shape. Re-scoping has a whole page worth keeping, so it keeps
 * it: the controls jump to what was asked for, the reads go out against that at once, and
 * the answers stay on screen dimmed until they are re-answered.
 *
 * The TITLE is the name resolved for the page's geography, not the catalogue one. The read
 * already falls back to `product.name` when nothing applies, so there is nothing to decide
 * here and the page is never untitled; the catalogue name stays in the breadcrumb, where it
 * names the catalogue path rather than the product.
 *
 * The NAMING section leads the main column: it describes the product's identity, and
 * everything below it describes the product's content. It takes the read's projection alone —
 * not the rail's filters, which narrow content, and a product's names are not content — and
 * needs no loading or error state, the page read having resolved before anything renders here.
 */
export function ProductPage() {
	const { productId } = useParams<{ productId: string }>()
	const { requested, setScope } = useGeoScope()
	const filters = useContentFilters()

	const { data: page, isLoading, isError, error, isPlaceholderData } = useProductPage(productId ?? null, requested)
	const [locale, setLocale] = useState<string | null>(null)
	const [channel, setChannel] = useState(ALL_CHANNELS)

	// Placeholder data IS the previous scope's page, so it is also the signal that the one
	// on screen has not been resolved yet.
	const resolving = isPlaceholderData
	const productCode = page?.product.code ?? null
	/**
	 * The geography the whole page agrees on: what the server resolved, except while a
	 * re-scoped read is in flight — the page then still describes the PREVIOUS scope, so the
	 * requested code stands in and every read starts one round trip earlier instead of
	 * queueing behind the page read. The code can only have come from the options the server
	 * offered, and when the resolution lands it takes over: a stale or retired code degrades
	 * the page to unscoped and only the server can say so.
	 */
	const market = resolving ? requestedGeo(page, requested) : resolvedGeo(page)
	const sections = useProductSections(page, productCode, market, filters.selection)
	const { rulesContext } = sections

	if (isLoading) return <ProductPageSkeleton />
	// An absent product is a dead end to state plainly; any other failure is worth a retry.
	if (isNotFound(error)) return <p className="text-ds-text-muted">Product not found.</p>
	if (isError) return <p className="text-ds-text-muted">Could not load this product. Please retry.</p>
	if (!page) return <p className="text-ds-text-muted">Product not found.</p>

	// Names the resolved scope wherever a result has to report what it answered.
	const scopeLabel = marketLabelOf(page)
	const isFiltered = Object.keys(filters.selection).length > 0

	return (
		<ProductPageLayout
			resolving={resolving}
			breadcrumb={<ProductBreadcrumb page={page} scope={market} />}
			header={<PageHeader eyebrow={PRODUCT_PAGE_EYEBROW} title={page.resolvedName} />}
			hierarchy={<ProductHierarchy page={page} scope={market} onScopeChange={setScope} isResolving={resolving} />}
			rail={
				<ProductFilterRail
					shownAxisCodes={filters.shownAxisCodes}
					selection={filters.selection}
					onToggle={filters.toggle}
					onAddAxis={filters.addAxis}
				/>
			}
			main={
				<ProductMainTabs
					page={page}
					scopeLabel={scopeLabel}
					isFiltered={isFiltered}
					locale={locale}
					onLocaleChange={setLocale}
					channel={channel}
					onChannelChange={setChannel}
					rulesHref={rulesLinkTo(rulesContext)}
					sections={sections}
					brandKitEmptyLabel={brandKitEmptyLabel(scopeLabel, isFiltered)}
					textsEmptyLabel={textsEmptyLabel(scopeLabel, isFiltered)}
					campaignsEmptyLabel={campaignsEmptyLabel(scopeLabel, isFiltered)}
				/>
			}
		/>
	)
}
